<template>
	<div class="space-y-1.5">
		<!-- The graph's rule in one line, so the tiles below can be read against it. -->
		<p v-if="ruleText" class="text-base-content/40 px-1 text-xs" :title="t('alerts.widget.ruleExplain')">
			{{ ruleText }}
		</p>

		<!-- A graph can carry well over a hundred equations; sticky so the filter
		     stays reachable while scrolling the list inside the modal. -->
		<div v-if="allItems.length > 5" class="bg-base-100 sticky top-0 z-10 flex items-center gap-2 pb-1">
			<input
				v-model="search"
				type="search"
				class="input input-sm min-w-0 flex-1"
				:placeholder="t('alerts.widget.filter')"
				:aria-label="t('alerts.widget.filter')" />
			<span v-if="search.trim()" class="text-base-content/50 shrink-0 text-xs">
				{{ t('alerts.widget.counted', { shown: items.length, total: allItems.length }) }}
			</span>
		</div>

		<!-- Grouped by what the rule says right now, loudest first. "OK" starts
		     collapsed and sits at the bottom: the list exists for the rows that
		     are NOT fine. -->
		<!-- daisyUI collapsibles, the same pattern as the SVG-export groups, so
		     they read as something that opens. The left edge carries the
		     group's colour, the title its icon and count. -->
		<div
			v-for="group in groups"
			:key="group.key"
			class="collapse collapse-arrow border-base-300 bg-base-100 group rounded-lg border border-l-4"
			:class="group.borderClass">
			<!-- The checkbox IS the daisyUI collapse: it owns open/closed, the
			     arrow and the transition. We only mirror it so "OK" can start
			     folded and rows can stay unrendered while a group is closed. -->
			<input
				type="checkbox"
				:checked="!collapsed[group.key]"
				:aria-label="group.label"
				@change="toggleGroup(group.key)" />
			<!-- Hover goes on the wrapper (`group`): the collapse's checkbox sits
			     invisibly over the title and would otherwise swallow the pointer. -->
			<div
				class="collapse-title bg-base-200 group-hover:bg-base-300 group-hover:text-base-content flex min-h-0 items-center gap-2 px-3 py-2 pr-10 text-sm font-semibold transition-colors duration-150">
				<component :is="group.icon" class="h-5 w-5 shrink-0" :class="group.iconClass" />
				<span class="flex-1">{{ group.label }}</span>
				<span class="badge badge-sm" :class="group.badgeClass">{{ group.rows.length }}</span>
			</div>
			<div class="collapse-content flex flex-col gap-1.5 px-2">
				<template v-if="!collapsed[group.key]">
			<div
				v-for="item in group.rows"
				:key="item.key"
				class="bg-base-200/40 flex flex-col gap-1.5 rounded px-3 py-2">
				<!-- Name in the sensor's own colour so the row can be matched to the
				     curve in the chart; the state sits at the far right, where the eye
				     lands after reading the name. -->
				<div class="flex flex-row items-center gap-2">
					<span
						class="h-2.5 w-2.5 shrink-0 rounded-full"
						:style="{ backgroundColor: item.color || 'currentColor' }"></span>
					<span
						class="min-w-0 flex-1 truncate text-sm font-semibold"
						:style="item.color ? { color: item.color } : undefined">
						{{ item.name }}
					</span>
					<small class="text-base-content/40 shrink-0 whitespace-nowrap text-xs">
						{{ formatDate(item.alert ? item.lastAlertDate : item.lastWarnDate) }}
					</small>
					<span class="badge badge-sm shrink-0 gap-1" :class="badgeClass(stateFor(item))" :title="stateTitle(item)">
						<component :is="stateIcon(stateFor(item))" class="h-3 w-3" />
						{{ stateLabel(stateFor(item)) }}
					</span>
					<!-- The cron writes the state every 15 minutes, the readings below are
					     read now. Right after new data arrives the two disagree — saying so
					     is the difference between "contradiction" and "not yet recalculated". -->
					<span v-if="pendingText(item)" class="badge badge-ghost badge-xs shrink-0 gap-1">
						<Clock class="h-3 w-3" />
						{{ stateLabel(storedStateFor(item)) }}
					</span>
				</div>

				<!-- One tile per judged unit: the deciding number on top, the remaining
				     statistics below it, and window / sample count on one line. Three
				     fixed columns, condensed type: the units of a rule stay on one row. -->
				<div class="font-condensed grid grid-cols-3 items-stretch gap-1.5">
					<template v-if="samplesFor(item).length">
						<!-- The tile is the summary; everything that went into it is one
						     hover away, as a table rather than a run-on tooltip string. -->
						<Floater
							v-for="sample in samplesFor(item)"
							:key="sample.ts"
							trigger-class="block min-w-0"
							hover
							placement="top">
							<span
								class="flex h-full min-w-0 cursor-default flex-col items-center rounded px-1.5 py-1 leading-tight"
								:class="sampleClass(sample.level)">
								<!-- Big: the number the limits were compared against. -->
								<span class="flex items-center gap-1.5">
									<component :is="levelIcon(sample.level)" class="h-4 w-4 shrink-0" />
									<span class="truncate text-base font-semibold tabular-nums">{{ formatValue(leadValue(sample)) }}</span>
								</span>
								<!-- Small: the statistics that did NOT decide, so an interval can
								     be read without switching the setting to see them. -->
								<span v-if="statsLine(sample)" class="max-w-full truncate text-xs tabular-nums opacity-60">
									{{ statsLine(sample) }}
								</span>
								<span class="flex max-w-full items-center gap-1 whitespace-nowrap text-xs opacity-50">
									{{ unitLabel(sample) }}
									<!-- How many readings went into this unit. As a word it took the
									     whole line; the stacked-layers icon carries it in a glyph. -->
									<template v-if="sample.n > 1">
										·
										<Layers class="h-3 w-3 shrink-0" :aria-label="readingCount(sample.n)" />
										{{ sample.n }}
									</template>
								</span>
							</span>
							<template #content>
								<table class="min-w-56 p-2 text-xs">
									<tbody>
										<tr v-for="row in detailRows(sample)" :key="row.label">
											<td class="pr-3 align-top opacity-60">{{ row.label }}</td>
											<td class="whitespace-nowrap text-right font-mono">{{ row.value }}</td>
										</tr>
									</tbody>
								</table>
							</template>
						</Floater>
						<!-- Keep the row three slots wide even with fewer intervals, so the
						     tiles of different equations still line up. -->
						<span v-for="n in Math.max(0, 3 - samplesFor(item).length)" :key="`pad-${n}`"></span>
					</template>
					<span v-else-if="!whyText(item)" class="text-base-content/40 col-span-3 py-1 text-xs italic">
						{{ t('alerts.widget.noReadings') }}
					</span>
				</div>

				<!-- Why there is no verdict, in one line: how many units the rule found
				     against how many it needs, and how old the newest one is. -->
				<p v-if="whyText(item)" class="bg-base-200/50 rounded px-2 py-1 text-xs opacity-70">
					<span class="font-semibold">{{ t('alerts.widget.whyTitle') }}:</span>
					{{ whyText(item) }}
				</p>
				<p v-else-if="pendingText(item)" class="text-base-content/40 text-xs italic">
					{{ pendingText(item) }}
				</p>

				<!-- Configured bands, on their own line so they read as limits rather
				     than as a footnote to the readings above. -->
				<div v-if="warnRange(item) || alertRange(item)" class="flex flex-wrap items-center gap-1">
					<span v-if="warnRange(item)" class="badge badge-xs badge-warning badge-soft gap-1 font-mono">
						<AlertTriangle class="h-3 w-3" />
						{{ t('alerts.widget.thresholdWarn', { range: warnRange(item) }) }}
					</span>
					<span v-if="alertRange(item)" class="badge badge-xs badge-error badge-soft gap-1 font-mono">
						<AlertOctagon class="h-3 w-3" />
						{{ t('alerts.widget.thresholdAlert', { range: alertRange(item) }) }}
					</span>
				</div>
			</div>
				</template>
			</div>
		</div>

		<p v-if="!items.length" class="text-base-content/60 py-8 text-center text-sm">
			{{ allItems.length ? t('alerts.widget.noMatches') : t('alerts.widget.empty') }}
		</p>
	</div>
</template>

<script setup lang="ts">
	import { computed, onMounted, ref, watch } from 'vue'
	import dayjs from 'dayjs'
	import { storeToRefs } from 'pinia'
	import { useI18n } from 'vue-i18n'
	import { AlertOctagon, AlertTriangle, CheckCircle, CircleSlash, Clock, Layers } from 'lucide-vue-next'
	import Floater from '@/components/common/Floater.vue'
	import { useAlertsStore } from '@/stores/alerts.store'
	import { useBaseStore } from '@/stores/base.store'
	import { bucketEndSec, formatBucketRange } from '@/libs/timeBuckets'
	import { useAlerts } from './useAlerts'
	import { _translations } from './_translations'
	import type {
		AlertAggregate,
		AlertSample,
		AlertSampleLevel,
		EquationAlertState,
		SensorAlertRow,
	} from './_types'

	// Kompakte Status-Liste der konfigurierten Alarme/Warnungen des offenen
	// Graphen. Zeilen und gespeicherter Zustand kommen aus `configuredAlerts`
	// (`alertData`, vom Cron geschrieben); das Live-Urteil, die beurteilten
	// Einheiten und die Grenzwerte aus `/api/v2/customgraphs/alert-states/:cgId`,
	// das dieselbe Funktion rechnet wie der Cron. Gemeinsam genutzt vom
	// AlertWidget-Panel und dem Alarme-Reiter im Alert-Flags-Modal.
	const { t } = useI18n(_translations)
	const alertsApi = useAlerts()
	const alertsStore = useAlertsStore()
	const baseStore = useBaseStore()
	const { alertStatesByGraph } = storeToRefs(alertsStore)
	const allItems = alertsApi.configuredAlerts

	// Matches the equation name as well as its serial — the name is what the
	// chart shows, the serial is what the API and `alertData` are keyed by.
	const search = ref('')
	const items = computed(() => {
		const q = search.value.trim().toLowerCase()
		if (!q) return allItems.value
		return allItems.value.filter((row) =>
			`${row.name || ''} ${row.equationSerial || ''}`.toLowerCase().includes(q),
		)
	})

	const activeGraphId = computed(() => baseStore.getActiveIds.graphId || '')

	// ---- status groups ----------------------------------------------------
	type GroupKey = 'alert' | 'warn' | 'noData' | 'ok'
	const GROUP_ORDER: GroupKey[] = ['alert', 'warn', 'noData', 'ok']
	const groupOf = (row: SensorAlertRow): GroupKey => {
		switch (stateFor(row)) {
			case 'alert':
				return 'alert'
			case 'warn':
			case 'warnPreAlert':
			case 'preAlert':
			case 'preWarn':
				return 'warn'
			case 'ok':
				return 'ok'
			default:
				return 'noData'
		}
	}
	const GROUP_STYLE: Record<GroupKey, { icon: unknown; iconClass: string; borderClass: string; badgeClass: string }> = {
		alert: { icon: AlertOctagon, iconClass: 'text-error', borderClass: 'border-l-error', badgeClass: 'badge-error' },
		warn: { icon: AlertTriangle, iconClass: 'text-warning', borderClass: 'border-l-warning', badgeClass: 'badge-warning' },
		noData: {
			icon: CircleSlash,
			iconClass: 'text-base-content/60',
			borderClass: 'border-l-base-content/30',
			badgeClass: 'badge-ghost',
		},
		ok: { icon: CheckCircle, iconClass: 'text-success', borderClass: 'border-l-success', badgeClass: 'badge-success badge-soft' },
	}
	const groups = computed(() => {
		const buckets: Record<GroupKey, SensorAlertRow[]> = { alert: [], warn: [], noData: [], ok: [] }
		for (const row of items.value) buckets[groupOf(row)].push(row)
		return GROUP_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
			key,
			label: t(`alerts.widget.groups.${key}`),
			rows: buckets[key],
			...GROUP_STYLE[key],
		}))
	})
	// Only the groups that need attention start open; "too few readings" is
	// mostly the long-dead sensors and would push "OK" out of sight. A group
	// the user opens or closes stays that way for the session.
	const collapsed = ref<Record<GroupKey, boolean>>({ alert: false, warn: false, noData: true, ok: true })
	const toggleGroup = (key: GroupKey) => {
		collapsed.value = { ...collapsed.value, [key]: !collapsed.value[key] }
	}

	const isIntervalMode = computed(
		() => alertStatesByGraph.value[activeGraphId.value]?.source === 'timekey',
	)
	const aggregate = computed<AlertAggregate>(
		() => alertStatesByGraph.value[activeGraphId.value]?.aggregate || 'minmax',
	)
	// The graph's rule, as one line above the list. Hidden when no equation has
	// limits configured — a rule stated over an empty list describes nothing.
	const ruleText = computed(() => {
		const states = alertStatesByGraph.value[activeGraphId.value]
		if (!states || !allItems.value.length) return ''
		if (states.source === 'timekey') {
			return t('alerts.widget.ruleTimekey', {
				n: states.consecutive,
				interval: states.interval,
				aggregate: t(aggregate.value === 'avg' ? 'alerts.widget.aggregateAvg' : 'alerts.widget.aggregateMinmax'),
			})
		}
		return t('alerts.widget.ruleDeep', { n: states.consecutive })
	})
	// The allowed span in whole units — seconds would say nothing to a reader.
	const formatSpan = (seconds: number): string => {
		if (seconds % 86400 === 0) return t('alerts.widget.spanDays', { n: seconds / 86400 })
		if (seconds % 3600 === 0) return t('alerts.widget.spanHours', { n: seconds / 3600 })
		return t('alerts.widget.spanMinutes', { n: Math.round(seconds / 60) })
	}

	// Units arrive with their START (backend `bucketStart`). An interval reads as
	// the window it covers — `20.10. 06:00–12:00`, 24h `Mo 20.10.` — the same
	// way the chart draws it at its end; a raw reading keeps its own time.
	const toSec = (value: number): number => (value < 1e12 ? value : Math.round(value / 1000))
	const unitEndSec = (startSec: number): number =>
		bucketEndSec(startSec, alertStatesByGraph.value[activeGraphId.value]?.interval || '')
	const unitLabel = (sample: AlertSample): string => {
		if (!sample.ts) return ''
		if (!isIntervalMode.value) return formatDate(sample.ts)
		const interval = alertStatesByGraph.value[activeGraphId.value]?.interval || ''
		const start = toSec(sample.ts)
		// Short form for the tile: full hours as `18–24`; the exact times are in
		// the tile's tooltip.
		return formatBucketRange(start, unitEndSec(start), interval).replace(/(\d{2}):00–(\d{2}):00$/, '$1–$2')
	}

	const readingCount = (n: number): string =>
		n === 1 ? t('alerts.widget.readingCountOne') : t('alerts.widget.readingCountMany', { n })

	/** Everything behind a tile, for the hover table. */
	const detailRows = (sample: AlertSample): Array<{ label: string; value: string }> => {
		const states = alertStatesByGraph.value[activeGraphId.value]
		const rows: Array<{ label: string; value: string }> = []
		if (isIntervalMode.value) {
			// End = start of the next interval: DST days and 24h at local midnight included.
			const span = states?.unitSeconds ?? 0
			rows.push({ label: t('alerts.widget.detailFrom'), value: formatDate(sample.ts) })
			if (span) rows.push({ label: t('alerts.widget.detailTo'), value: formatDate(unitEndSec(toSec(sample.ts))) })
		} else {
			rows.push({ label: t('alerts.widget.detailAt'), value: formatDate(sample.ts) })
		}
		rows.push({ label: t('alerts.widget.detailAge'), value: formatRelative(sample.ts) })
		if (sample.n > 1) {
			rows.push({ label: t('alerts.widget.detailMin'), value: formatValue(sample.min) })
			rows.push({ label: t('alerts.widget.detailAvg'), value: formatValue(sample.avg) })
			rows.push({ label: t('alerts.widget.detailMax'), value: formatValue(sample.max) })
			rows.push({ label: t('alerts.widget.detailCount'), value: String(sample.n) })
		} else {
			rows.push({ label: t('alerts.widget.detailValue'), value: formatValue(sample.avg) })
		}
		rows.push({ label: t('alerts.widget.detailLevel'), value: t(`alerts.widget.levels.${sample.level}`) })
		return rows
	}

	// The statistics the big number is NOT: with `avg` deciding, min and max are
	// still the interesting part of an interval, and vice versa. A single reading
	// has no spread, so nothing is shown for it.
	const statsLine = (sample: AlertSample): string => {
		if (!isIntervalMode.value || sample.n <= 1) return ''
		// `94.771 … 95.746` reads as a range without needing the two labels.
		if (aggregate.value === 'avg') return `${formatValue(sample.min)} … ${formatValue(sample.max)}`
		return `${t('alerts.widget.statAvg')} ${formatValue(sample.avg)}`
	}

	// The number the thresholds were compared against, so the big figure in a
	// tile is the one that decided — not a mean next to a breach caused by a
	// single spike. For min/max that is whichever extreme is further from zero.
	const leadValue = (sample: AlertSample): number =>
		aggregate.value === 'avg'
			? sample.avg
			: Math.abs(sample.max) >= Math.abs(sample.min)
			  ? sample.max
			  : sample.min

	const infoByEquation = computed<Record<string, EquationAlertState>>(() => {
		const states = alertStatesByGraph.value[activeGraphId.value]
		const map: Record<string, EquationAlertState> = {}
		for (const eq of states?.equations || []) map[eq.serial] = eq
		return map
	})
	const infoFor = (row: SensorAlertRow): EquationAlertState | null =>
		(row.equationSerial && infoByEquation.value[row.equationSerial]) || null
	const samplesFor = (row: SensorAlertRow): AlertSample[] => infoFor(row)?.samples ?? []
	// When the stored verdict was computed — the badge comes from the alert cron
	// (every 15 min), the readings below are read live, so the two can and do
	// disagree. Without the timestamp that looks like a contradiction.
	const stateTitle = (row: SensorAlertRow): string => {
		const info = infoFor(row)
		const parts: string[] = []
		if (info?.modified) {
			parts.push(
				t('alerts.widget.stateComputed', {
					ago: formatRelative(info.modified),
					date: formatDate(info.modified),
				}),
			)
		} else {
			parts.push(t('alerts.widget.stateNever'))
		}
		// The explanation block already carries the detail; the tooltip only adds
		// the shortfall in one line for the collapsed case.
		const states = alertStatesByGraph.value[activeGraphId.value]
		if (states && info && stateFor(row) === 'no data') {
			parts.push(t('alerts.widget.stateTooFew', { have: info.judgedCount, need: states.consecutive }))
		}
		return parts.join(' · ')
	}

	// 'no data' is the state machine's word for "fewer usable units than the rule
	// needs", not "nothing arrived" — one line says which, and how far off.
	const whyText = (row: SensorAlertRow): string => {
		if (stateFor(row) !== 'no data') return ''
		const states = alertStatesByGraph.value[activeGraphId.value]
		const info = infoFor(row)
		if (!info || !states) return ''
		const unit =
			states.source === 'timekey'
				? t('alerts.widget.whyUnitTimekey', { interval: states.interval })
				: t('alerts.widget.whyUnitDeep')
		const parts = [
			t('alerts.widget.whyCompact', {
				have: info.samples.length,
				need: states.consecutive,
				unit,
				span: formatSpan(info.spanSeconds),
			}),
		]
		if (info.newestUnitTs) {
			if (states.source === 'timekey') {
				const start = toSec(info.newestUnitTs)
				parts.push(t('alerts.widget.whyNewestUnit', { range: formatBucketRange(start, unitEndSec(start), states.interval) }))
			} else {
				parts.push(t('alerts.widget.whyNewest', { ago: formatRelative(info.newestUnitTs) }))
			}
		}
		const pending = pendingText(row)
		if (pending) parts.push(pending)
		return parts.join(' · ')
	}

	/** Set only while the stored verdict is older than what the data now says. */
	const pendingText = (row: SensorAlertRow): string => {
		const info = infoFor(row)
		if (!info) return ''
		const stored = String(info.state || '')
		if (!stored || stored === String(info.liveState || '')) return ''
		return t('alerts.widget.whyPending', {
			state: stateLabel(stored),
			ago: info.modified ? formatRelative(info.modified) : t('alerts.widget.stateNever'),
		})
	}

	const storedStateFor = (row: SensorAlertRow): string => {
		const info = infoFor(row)
		return String(info?.state || '')
	}

	// What the rule says about the data as it stands. The stored verdict drives
	// the emails and is shown beside it when the two have not met yet.
	const stateFor = (row: SensorAlertRow): string => {
		const info = infoFor(row)
		return String(info?.liveState || info?.state || row.state || '')
	}

	const thresholdsByEquation = computed(() => {
		const states = alertStatesByGraph.value[activeGraphId.value]
		const map: Record<string, { warn: string; alert: string }> = {}
		const range = (min: number | null, max: number | null) => {
			if (min === null && max === null) return ''
			return `${min ?? '−∞'} … ${max ?? '∞'}`
		}
		for (const eq of states?.equations || []) {
			map[eq.serial] = {
				warn: range(eq.thresholds.warnMin, eq.thresholds.warnMax),
				alert: range(eq.thresholds.alertMin, eq.thresholds.alertMax),
			}
		}
		return map
	})
	const warnRange = (row: SensorAlertRow): string =>
		(row.equationSerial && thresholdsByEquation.value[row.equationSerial]?.warn) || ''
	const alertRange = (row: SensorAlertRow): string =>
		(row.equationSerial && thresholdsByEquation.value[row.equationSerial]?.alert) || ''

	const STATE_KEYS = new Set([
		'alert',
		'warnPreAlert',
		'preAlert',
		'warn',
		'preWarn',
		'ok',
		'no data',
	])
	const stateLabel = (state?: string | null) => {
		const key = String(state || '')
		return STATE_KEYS.has(key) ? t(`alerts.widget.states.${key}`) : t('alerts.widget.states.unknown')
	}
	const badgeClass = (state?: string | null) => {
		switch (String(state || '')) {
			case 'alert':
				return 'badge-error'
			case 'warnPreAlert':
			case 'preAlert':
				return 'badge-error badge-soft'
			case 'warn':
				return 'badge-warning'
			case 'preWarn':
				return 'badge-warning badge-soft'
			case 'ok':
				return 'badge-success badge-soft'
			default:
				// 'no data' and never-analyzed: neither good nor bad news.
				return 'badge-ghost'
		}
	}
	// The badge carries its own symbol: the colour alone is easy to miss in a
	// long list, and in the two outline variants it is faint by design.
	const stateIcon = (state?: string | null) => {
		switch (String(state || '')) {
			case 'alert':
			case 'warnPreAlert':
			case 'preAlert':
				return AlertOctagon
			case 'warn':
			case 'preWarn':
				return AlertTriangle
			case 'ok':
				return CheckCircle
			default:
				return CircleSlash
		}
	}
	// Same symbols on the readings themselves — which of the three pushed the
	// equation over should be readable without comparing against the limits below.
	const levelIcon = (level: AlertSampleLevel) => {
		switch (level) {
			case 'alert':
				return AlertOctagon
			case 'warn':
				return AlertTriangle
			case 'ignored':
				return CircleSlash
			default:
				return CheckCircle
		}
	}
	const sampleClass = (level: AlertSampleLevel) => {
		switch (level) {
			case 'alert':
				return 'bg-error/20 text-error'
			case 'warn':
				return 'bg-warning/20 text-warning'
			case 'ignored':
				return 'bg-base-200 text-base-content/40 line-through'
			default:
				return 'bg-base-200 text-base-content/70'
		}
	}

	// Timestamps kommen je nach Quelle in Sekunden oder Millisekunden — beides abfangen.
	const formatDate = (value?: number | null) => {
		if (!value) return ''
		const ms = value < 1e12 ? value * 1000 : value
		const date = dayjs(ms)
		return date.isValid() ? date.format('DD.MM. HH:mm') : ''
	}
	// „vor 4 Stunden" / „4 hours ago" — dayjs' relativeTime ist global geladen
	// und folgt der eingestellten Sprache (siehe main.ts).
	const formatRelative = (value?: number | null) => {
		if (!value) return ''
		const ms = value < 1e12 ? value * 1000 : value
		const date = dayjs(ms)
		return date.isValid() ? date.fromNow() : ''
	}

	// Fünf signifikante Stellen reichen; lange Nachkommaketten machen die Zeile unlesbar.
	const formatValue = (value: number) => {
		if (!Number.isFinite(value)) return '–'
		const rounded = Number(value.toPrecision(5))
		return String(rounded)
	}

	onMounted(() => {
		void alertsApi.init()
		if (activeGraphId.value) void alertsStore.fetchAlertStates(activeGraphId.value)
	})
	// Das Panel bleibt beim Graphwechsel montiert — die Messwerte müssen mit.
	watch(activeGraphId, (id) => {
		if (id) void alertsStore.fetchAlertStates(id)
	})
</script>
